/**
 * Company Service
 *
 * Fetches and updates the current tenant's company profile from the backend.
 */

import type { Company } from '../types';
import { LocationService } from '@/entities/location/services/locationService';
import { apiClient } from '@/shared/services/api';
import { COUNTRIES } from '@/shared/constants/countries';

// ── Backend shape (camelCase after apiClient transform) ───────────────────────

interface BackendCompany {
  id: string;
  name: string;
  legalName: string;
  taxId: string;
  email: string;
  phone: string;
  website: string;
  logoUrl?: string;
  street?: string;
  city: string;
  state?: string;
  postalCode?: string;
  country: string;
  baseCurrency: string;
  timezone: string;
  brandColor: string;
  plan: string;
  isActive: boolean;
  createdAt: string;
}

function normalizeCountryCode(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const normalized = value.trim();
  const byCode = COUNTRIES.find((country) => country.code === normalized.toUpperCase());
  if (byCode) return byCode.code;
  const byName = COUNTRIES.find((country) => country.nameEn.toLowerCase() === normalized.toLowerCase());
  return byName?.code;
}

function mapBackendCompany(b: BackendCompany): Company {
  return {
    id: b.id,
    name: b.name,
    legalName: b.legalName || undefined,
    slug: b.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    schemaName: '',
    taxId: b.taxId || undefined,
    baseCurrency: b.baseCurrency || 'EUR',
    logo: b.logoUrl || undefined,
    address: {
      street: b.street || undefined,
      city: b.city || undefined,
      state: b.state || undefined,
      postalCode: b.postalCode || undefined,
      country: normalizeCountryCode(b.country) || b.country || undefined,
    },
    contact: (b.email || b.phone || b.website) ? {
      email: b.email || undefined,
      phone: b.phone || undefined,
      website: b.website || undefined,
    } : undefined,
    settings: {
      timezone: b.timezone || 'UTC',
    },
    brandColor: b.brandColor || undefined,
    isActive: b.isActive,
    createdAt: new Date(b.createdAt),
  };
}

// ── Service ───────────────────────────────────────────────────────────────────

export const CompanyService = {
  async getCompany(): Promise<Company> {
    const response = await apiClient.get<BackendCompany>('/api/v1/company/', {
      skipLocationFilter: true,
    });
    return mapBackendCompany(response.data);
  },

  async updateCompany(data: Partial<{
    name: string;
    legalName: string;
    taxId: string;
    email: string;
    phone: string;
    website: string;
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    baseCurrency: string;
    timezone: string;
    logoUrl: string;
    brandColor: string;
  }>): Promise<Company> {
    const payload: Record<string, unknown> = {};
    if (data.name !== undefined)         payload.name = data.name;
    if (data.legalName !== undefined)    payload.legal_name = data.legalName;
    if (data.taxId !== undefined)        payload.tax_id = data.taxId;
    if (data.email !== undefined)        payload.email = data.email;
    if (data.phone !== undefined)        payload.phone = data.phone;
    if (data.website !== undefined)      payload.website = data.website;
    if (data.logoUrl !== undefined)      payload.logo_url = data.logoUrl;
    if (data.street !== undefined)       payload.street = data.street;
    if (data.city !== undefined)         payload.city = data.city;
    if (data.state !== undefined)        payload.state = data.state;
    if (data.postalCode !== undefined)   payload.postal_code = data.postalCode;
    if (data.country !== undefined)      payload.country = data.country;
    if (data.baseCurrency !== undefined) payload.base_currency = data.baseCurrency;
    if (data.timezone !== undefined)     payload.timezone = data.timezone;
    if (data.brandColor !== undefined)   payload.brand_color = data.brandColor;

    const response = await apiClient.patch<BackendCompany>('/api/v1/company/', payload, {
      skipLocationFilter: true,
    });
    return mapBackendCompany(response.data);
  },

};
